<template>
  <div class="accounts-view">
    <h2>Linked Accounts</h2>

    <p v-if="loading">Loading accounts...</p>
    <p v-if="error" class="error">{{ error }}</p>

    <div v-if="!loading && !error && displayItems.length === 0" class="no-accounts">
      <p>No accounts linked yet.</p>
      <router-link to="/link" class="link-button">Link an Account</router-link>
    </div>

    <ul v-if="displayItems.length > 0" class="items-list">
      <li v-for="item in displayItems" :key="item.item_id" class="item" :class="{ 'item-invalid': debugMode && !item.valid }">
        <div class="item-info">
          <span class="institution">{{ item.institution_name }}</span>
          <span v-if="item.error" class="item-error">Needs attention</span>
          <span v-if="debugMode" class="item-status" :class="item.valid ? 'status-valid' : 'status-invalid'">
            {{ item.valid ? 'valid' : 'invalid' }}
          </span>
          <span class="item-dates">
            <span v-if="item.last_updated">Updated: {{ formatDate(item.last_updated) }}</span>
            <span v-if="item.created">Created: {{ formatDate(item.created) }}</span>
          </span>
        </div>
        <router-link :to="'/link?item_id=' + item.item_id" class="refresh-link">
          Refresh Credentials
        </router-link>
      </li>
    </ul>

    <div v-if="displayItems.length > 0" class="actions">
      <router-link to="/link" class="link-button">Link Another Account</router-link>
    </div>

    <div v-if="debugMode" class="debug-info">
      <p>Debug mode: showing {{ items.length }} total items ({{ validItems.length }} valid)</p>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { get } from '@/common/api';

export default defineComponent({
  name: "AccountsView",
  data() {
    return {
      items: [],
      loading: true,
      error: null,
      debugMode: false,
    };
  },
  computed: {
    validItems() {
      return this.items.filter(item => item.valid);
    },
    displayItems() {
      return this.debugMode ? this.items : this.validItems;
    }
  },
  methods: {
    fetchItems() {
      get('items')
        .then(result => result.json())
        .then(data => {
          this.items = data.items || data || [];
          this.loading = false;
        })
        .catch(err => {
          this.error = 'Failed to load accounts: ' + err.message;
          this.loading = false;
        });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    }
  },
  mounted() {
    this.debugMode = localStorage.getItem('debug') === 'true';
    this.fetchItems();
  },
});
</script>

<style scoped>
.accounts-view {
  max-width: 600px;
  margin: 2rem auto;
  padding: 1rem;
}

.accounts-view h2 {
  margin-bottom: 1.5rem;
}

.items-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.institution {
  font-weight: 500;
}

.item-error {
  color: #dc3545;
  font-size: 0.875rem;
}

.item-dates {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.refresh-link {
  color: #0066cc;
  text-decoration: none;
}

.refresh-link:hover {
  text-decoration: underline;
}

.no-accounts {
  text-align: center;
  padding: 2rem;
}

.actions {
  margin-top: 1.5rem;
  text-align: center;
}

.link-button {
  display: inline-block;
  background-color: #0066cc;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 4px;
  text-decoration: none;
}

.link-button:hover {
  background-color: #0052a3;
}

.error {
  color: #dc3545;
}

.item-invalid {
  opacity: 0.6;
  border-style: dashed;
}

.item-status {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 3px;
}

.status-valid {
  background-color: #d4edda;
  color: #155724;
}

.status-invalid {
  background-color: #f8d7da;
  color: #721c24;
}

.debug-info {
  margin-top: 1.5rem;
  padding: 0.75rem;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #856404;
}
</style>
